import { ViewTitle } from 'components/ViewTitle';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../redux/actions';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import {
  Box,
  Typography,
  Paper,
  Collapse,
  IconButton,
  Divider,
  Avatar,
  Button,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import {
  FaPlus,
  FaTimes,
  FaPaperPlane,
  FaEdit,
  FaLink,
  FaCommentDots,
  FaTrash,
  FaUser,
  FaFile,
  FaDownload,
  FaFilePdf,
  FaFileImage,
  FaFileWord,
  FaFileExcel,
} from 'react-icons/fa';
import _ from 'lodash';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/th';

dayjs.extend(relativeTime);
dayjs.locale('th');

export default function ClassroomDetail({ title, subtitle }) {
  const dispatch = useDispatch();
  const { id } = useParams();
  const subject = useSelector((state) => state.subject);
  const me = useSelector((state) => state.me);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm();

  useEffect(() => {
    dispatch(actions.subjectGet(id));
    dispatch(actions.meGet());
  }, []);

  // เพิ่มฟังก์ชันสำหรับกำหนดไอคอนไฟล์
  const getFileIcon = (fileName) => {
    if (!fileName) return <FaFile />;

    const extension = fileName.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return <FaFilePdf className="text-red-600" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FaFileImage className="text-green-600" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="text-blue-600" />;
      case 'xls':
      case 'xlsx':
        return <FaFileExcel className="text-green-700" />;
      default:
        return <FaFile className="text-gray-600" />;
    }
  };

  // เพิ่มฟังก์ชันสำหรับแปลงขนาดไฟล์
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';

    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));

    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // ฟังก์ชันตรวจสอบสิทธิ์ลบโพสต์

  const renderDetail = () => (
    <Paper className="shadow-sm rounded-lg overflow-hidden mb-6">
      <Box className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b border-gray-100">
        <Typography variant="h6" className="font-medium text-gray-800">
          ห้องเรียนวิชา {subject?.name}
        </Typography>
      </Box>
      <Box className="p-4">
        <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Box>
            <Typography variant="body2" color="textSecondary">
              ระดับ
            </Typography>
            <Typography variant="body1" className="font-medium">
              {subject?.level?.name}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">
              คอร์ส
            </Typography>
            <Typography variant="body1" className="font-medium">
              {subject?.course?.name}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  const renderPost = () => (
    <Paper className="shadow-sm rounded-lg overflow-hidden">
      <Box className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 border-b border-gray-100">
        <Typography variant="h6" className="font-medium text-gray-800">
          โพสต์ทั้งหมด
        </Typography>
      </Box>

      {_.isEmpty(subject?.postMessages) ? (
        <Box className="p-8 text-center">
          <FaCommentDots className="text-gray-300 text-4xl mb-4 mx-auto" />
          <Typography color="textSecondary" variant="h6" className="mb-2">
            ยังไม่มีโพสต์ในห้องเรียนนี้
          </Typography>
          <Typography color="textSecondary" variant="body2">
            เริ่มสนทนาแรกในห้องเรียนของคุณ
          </Typography>
        </Box>
      ) : (
        <Box className="divide-y divide-gray-100">
          {subject?.postMessages?.map((post) => (
            <Box
              key={post._id}
              className="p-4 hover:bg-gray-50 transition-colors"
            >
              {/* Header ของโพสท์ */}
              <Box className="flex items-start justify-between mb-3">
                <Box className="flex items-center gap-3">
                  <Avatar
                    src={post?.poster?.image?.url}
                    alt={post?.poster?.firstname}
                    sx={{ width: 40, height: 40 }}
                  >
                    <FaUser />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="body2"
                      className="font-medium text-gray-800"
                    >
                      {post?.poster?.firstname} {post?.poster?.lastname}
                      {post?.poster?.nickname && (
                        <span className="text-gray-500">
                          {' '}
                          ({post?.poster?.nickname})
                        </span>
                      )}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {dayjs(post?.createdAt).fromNow()}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* เนื้อหาโพสต์ */}
              <Box className="ml-12">
                <Typography
                  variant="h6"
                  className="font-medium text-gray-800 mb-2"
                >
                  {post?.topic}
                </Typography>

                <Typography
                  variant="body1"
                  className="text-gray-600 mb-3 leading-relaxed"
                >
                  {post?.message}
                </Typography>

                {/* แสดงลิงก์แนบ */}
                {post?.link && (
                  <Box className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400 mb-3">
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      className="block mb-1"
                    >
                      ลิงก์แนบ:
                    </Typography>
                    <a
                      href={post?.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                    >
                      {post?.link}
                    </a>
                  </Box>
                )}

                {/* แสดงไฟล์แนบ */}
                {post?.file && (
                  <Box className="p-3 bg-gray-50 rounded-lg border border-gray-200 mb-3">
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      className="block mb-2"
                    >
                      ไฟล์แนบ:
                    </Typography>

                    <Box className="flex items-center justify-between p-3 bg-white rounded border hover:shadow-md transition-shadow">
                      {/* ข้อมูลไฟล์ */}
                      <Box className="flex items-center gap-3 flex-1">
                        <Box className="flex-shrink-0">
                          {getFileIcon(post?.file?.originalname)}
                        </Box>

                        <Box className="min-w-0 flex-1">
                          <Typography
                            variant="body2"
                            className="font-medium text-gray-800 truncate"
                            title={post?.file?.originalname}
                          >
                            {post?.file?.file_name || 'ไฟล์แนบ'}
                          </Typography>

                          <Box className="flex items-center gap-2 text-xs text-gray-500">
                            {post?.file?.size && (
                              <span>{formatFileSize(post?.file?.size)}</span>
                            )}

                            {post?.file?.mimetype && (
                              <span className="uppercase">
                                {post?.file?.mimetype.split('/')[1]}
                              </span>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* ปุ่มดาวน์โหลด */}
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FaDownload />}
                        onClick={() => {
                          if (post?.file?.url) {
                            window.open(post.file.url, '_blank');
                          }
                        }}
                        className="ml-3 flex-shrink-0"
                      >
                        ดาวน์โหลด
                      </Button>
                    </Box>

                    {/* แสดง URL ไฟล์ (สำหรับ debug หรือกรณีพิเศษ) */}
                    {post?.file?.url && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        className="block mt-2 break-all"
                      >
                        URL: {post?.file?.url}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* แสดงรูปภาพแนบ (ถ้ามี) */}
                {post?.image && (
                  <Box className="mt-3">
                    <img
                      src={post?.image?.url}
                      alt="รูปภาพแนบ"
                      className="max-w-full h-auto rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => window.open(post?.image?.url, '_blank')}
                      style={{ maxHeight: '300px' }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );

  const renderTitle = () => <ViewTitle title={title} subtitle={subtitle} />;

  return (
    <Box>
      {renderTitle()}
      {renderDetail()}
      {renderPost()}
    </Box>
  );
}
